import {
  ActuacionesAU,
  IndicadoresAU,
  ProjectAU,
  Translation,
} from "@/types/types";
import { normalizeLanguageCode } from "./common";

type TranslationLike = Partial<Translation> & { lenguajes_code?: unknown };
type Translatable = { translations?: unknown[] };

const base = (lang: string) => lang.split("-")[0];

const pickTranslation = (
  translations: TranslationLike[] | undefined,
  userLang: string
) => {
  if (!translations?.length) return undefined;
  const want = normalizeLanguageCode(userLang);
  const wantBase = base(want);

  let tr = translations.find(
    (t) => normalizeLanguageCode(t.lenguajes_code) === want
  );
  if (tr) return tr;

  tr = translations.find(
    (t) => base(normalizeLanguageCode(t.lenguajes_code)) === wantBase
  );
  return tr;
};

const getTranslations = (value: unknown) => {
  if (!value || typeof value !== "object") return undefined;
  const translations = (value as Translatable).translations;
  return Array.isArray(translations)
    ? (translations as TranslationLike[])
    : undefined;
};

export function pickTranslatedField(
  baseValue: string | undefined,
  translations: TranslationLike[] | null | undefined,
  field: keyof Translation = "nombre",
  preferLang?: string,
  fallbackOrder = ["es-ES", "en-US", "ca-ES", "ga-ES", "eu-ES"]
): string {
  const rows = translations ?? [];
  const want = normalizeLanguageCode(preferLang, "es-ES");

  const byLang = (lang: string) =>
    rows.find(
      (t) =>
        normalizeLanguageCode(t.lenguajes_code) === lang &&
        typeof t[field] === "string" &&
        t[field]
    );

  let hit = byLang(want);
  if (hit) return String(hit[field]);

  const wantBase = base(want);
  hit = rows.find(
    (t) =>
      base(normalizeLanguageCode(t.lenguajes_code)) === wantBase &&
      typeof t[field] === "string" &&
      t[field]
  );
  if (hit) return String(hit[field]);

  for (const lang of fallbackOrder) {
    hit = byLang(normalizeLanguageCode(lang));
    if (hit) return String(hit[field]);
  }

  return baseValue ?? "";
}

export function applyUserTranslationsDeep(
  projects: ProjectAU[],
  userLang: string
): ProjectAU[] {
  const normUser = normalizeLanguageCode(userLang);

  const translateActuacion = (a: ActuacionesAU): ActuacionesAU => {
    const tr = pickTranslation(getTranslations(a), normUser);
    if (!tr) return a;
    return {
      ...a,
      nombre: (tr.nombre as string | undefined) ?? a.nombre,
    };
  };

  const translateIndicador = (i: IndicadoresAU): IndicadoresAU => {
    const tr = pickTranslation(getTranslations(i), normUser);
    if (!tr) return i;
    return {
      ...i,
      descripcion:
        (tr.descripcion as string | undefined) ?? i.descripcion,
    };
  };

  return projects.map((p) => {
    const pTr = pickTranslation(getTranslations(p), normUser);
    const baseProject: ProjectAU = pTr
      ? {
          ...p,
          nombre: (pTr.nombre as string | undefined) ?? p.nombre,
          descripcion:
            (pTr.descripcion as string | undefined) ?? p.descripcion,
          beneficiarios:
            (pTr.beneficiarios as string | undefined) ?? p.beneficiarios,
          observaciones:
            (pTr.observaciones as string | undefined) ?? p.observaciones,
          actores: (pTr.actores as string | undefined) ?? p.actores,
          potenciales_fuentes_financiacion:
            (pTr.potenciales_fuentes_financiacion as string | undefined) ??
            p.potenciales_fuentes_financiacion,
          area: (pTr.area as string | undefined) ?? p.area,
          dimension: (pTr.dimension as string | undefined) ?? p.dimension,
        }
      : p;
    const actuaciones = (p.actuaciones ?? []).map(translateActuacion);
    const indicadores = (p.indicadores ?? []).map(translateIndicador);

    return {
      ...baseProject,
      actuaciones,
      indicadores,
    };
  });
}

export function applyUserTranslationsAllTypes<T extends { translations?: unknown[] }>(
  items: T[],
  userLang: string,
  keys?: readonly (keyof T & string)[]
): T[] {
  const norm = normalizeLanguageCode(userLang);
  return items.map((item) => {
    const tr = pickTranslation(getTranslations(item), norm);
    if (!tr) return item;

    const patch: Partial<T> = {};
    if (keys && keys.length) {
      for (const k of keys) {
        const value = (tr as Record<string, unknown>)[k];
        if (k in tr && value !== undefined) {
          patch[k] = value as T[typeof k];
        }
      }
    } else {
      for (const k of Object.keys(tr)) {
        if (k === "lenguajes_code") continue;
        const value = (tr as Record<string, unknown>)[k];
        if (k in item && value !== undefined) {
          patch[k as keyof T] = value as T[keyof T];
        }
      }
    }
    return { ...item, ...patch };
  });
}
